import { useMemo, useState } from "react";
import { DeleteOutlined, HolderOutlined, PlusOutlined, SaveOutlined } from "@ant-design/icons";
import { closestCenter, DndContext, PointerSensor, useSensor, useSensors, type DragEndEvent } from "@dnd-kit/core";
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Switch,
  Tabs,
  Tag,
  Typography,
} from "antd";
import dayjs from "dayjs";

import { ProductType } from "@fitness-app/data-models/entities/Product";
import {
  CheckoutVariantStatus,
  CheckoutVariantTarget,
  FunnelStepType,
  normalizeCohortSeatsConfig,
  type CheckoutStepPhoneField,
  type CohortSeatsConfig,
  type FunnelRewardConfig,
  type PageConfiguration,
  type SalesFunnel,
  type SalesFunnelStep,
} from "@fitness-app/data-models/entities/SalesFunnel";
import { useProductPricesOptions } from "~/modules/Products/hooks/useProductPricesOptions";
import { useAppSelector } from "~/store/initializeStore";
import { FunnelStepPageSelect } from "./FunnelStepPageSelect";
import { normalizeFunnelStepPath } from "./funnelStepPath";
import { slugifyForSave, slugifyWhileTyping } from "./slugInput";

type EditableStep = SalesFunnelStep;

const DEFAULT_COHORT_SEATS: CohortSeatsConfig = { mode: "real", limit: 30, start: 24, floor: 3 };

/** Seats shown in a weekly group (Reset landing): real paid orders or a simulated countdown. */
const CohortSeatsFields = ({
  value,
  onChange,
}: {
  value: CohortSeatsConfig | null | undefined;
  onChange: (value: CohortSeatsConfig) => void;
}) => {
  const config = normalizeCohortSeatsConfig({ ...DEFAULT_COHORT_SEATS, ...value });
  // Lowering the limit pulls Monday and Sunday down with it, so the countdown stays consistent.
  const patch = (next: Partial<CohortSeatsConfig>) => onChange(normalizeCohortSeatsConfig({ ...config, ...next }));

  return (
    <>
      <Typography.Title level={5}>Miejsca w grupie</Typography.Title>
      <div className="grid grid-cols-2 gap-4">
        <Form.Item label="Licznik miejsc">
          <Select
            value={config.mode}
            onChange={(mode: CohortSeatsConfig["mode"]) => patch({ mode })}
            options={[
              { label: "Prawdziwe zamówienia", value: "real" },
              { label: "Symulowany", value: "simulated" },
            ]}
          />
        </Form.Item>
        <Form.Item label="Miejsc w grupie">
          <InputNumber
            min={1}
            precision={0}
            className="w-full"
            value={config.limit}
            onChange={(limit) => patch({ limit: limit ?? 30 })}
          />
        </Form.Item>
      </div>
      {config.mode === "simulated" ? (
        <div className="grid grid-cols-2 gap-4">
          <Form.Item label="Wolnych w poniedziałek">
            <InputNumber
              min={1}
              precision={0}
              max={config.limit}
              className="w-full"
              value={config.start}
              onChange={(start) => patch({ start: start ?? config.limit })}
            />
          </Form.Item>
          <Form.Item label="Wolnych w niedzielę wieczorem">
            <InputNumber
              min={1}
              precision={0}
              max={config.start ?? config.limit}
              className="w-full"
              value={config.floor}
              onChange={(floor) => patch({ floor: floor ?? 1 })}
            />
          </Form.Item>
        </div>
      ) : null}
    </>
  );
};

/** The challenge reward the Bikini landing names in zł: its own amount, another one or none. */
const RewardFields = ({
  value,
  onChange,
}: {
  value: FunnelRewardConfig | null | undefined;
  onChange: (value: FunnelRewardConfig) => void;
}) => {
  const mode = value?.mode ?? "default";

  return (
    <>
      <Typography.Title level={5}>Nagroda za metamorfozę</Typography.Title>
      <div className="grid grid-cols-2 gap-4">
        <Form.Item label="Kwota na stronie">
          <Select
            value={mode}
            onChange={(next: FunnelRewardConfig["mode"]) => onChange({ mode: next, amount: value?.amount ?? null })}
            options={[
              { label: "Domyślna (441 zł)", value: "default" },
              { label: "Własna kwota", value: "custom" },
              { label: "Ukryta", value: "hidden" },
            ]}
          />
        </Form.Item>
        {mode === "custom" ? (
          <Form.Item label="Kwota nagrody" required>
            <InputNumber
              min={1}
              precision={0}
              addonAfter="zł"
              className="w-full"
              value={value?.amount ?? null}
              onChange={(amount) => onChange({ mode, amount })}
            />
          </Form.Item>
        ) : null}
      </div>
    </>
  );
};

export interface SalesFunnelFormModel {
  activeUntil: number | null;
  cartSettings: Record<string, unknown> | null;
  default: boolean;
  excluded: boolean;
  metadata: Record<string, unknown>;
  name: string;
  slug: string;
  status: CheckoutVariantStatus;
  steps: EditableStep[];
  target: CheckoutVariantTarget;
}

interface ProductSalesFunnelFormProps {
  funnel?: SalesFunnel | null;
  isSaving?: boolean;
  onCancel: () => void;
  onSubmit: (model: SalesFunnelFormModel) => void;
  open: boolean;
  productId: string;
}

const createEmptyPageConfiguration = (): PageConfiguration => ({
  productNameOverride: "",
  productImageOverride: "",
  productDescriptionOverride: "",
  pricingDisabled: false,
  timer: false,
  promoVideo: undefined,
});

const getTimerConfig = (config: PageConfiguration) =>
  config.timer || {
    title: "Oferta specjalna",
    description: "Opis oferty",
    startInfo: "Oferta kończy się za:",
  };

const createStep = (type: FunnelStepType, productId: string, order: number): EditableStep => {
  const now = new Date().toISOString();
  const base = {
    id: crypto.randomUUID(),
    createdAt: now,
    updatedAt: now,
    name: `Krok ${order}`,
    description: "",
    path: type === FunnelStepType.ThankYou ? "ty" : type === FunnelStepType.Landing ? "start" : "checkout",
    type,
  };

  if (type === FunnelStepType.Landing) {
    return {
      ...base,
      type,
      stepConfig: {
        productId,
        pageConfiguration: createEmptyPageConfiguration(),
      },
    };
  }

  if (type === FunnelStepType.Checkout) {
    return {
      ...base,
      type,
      stepConfig: {
        productId,
        prices: [],
        pageConfiguration: createEmptyPageConfiguration(),
      },
    };
  }

  if (type === FunnelStepType.ThankYou) {
    return {
      ...base,
      type,
      stepConfig: {
        productId,
        pageConfiguration: createEmptyPageConfiguration(),
      },
    };
  }

  return {
    ...base,
    type,
    stepConfig: {
      productId,
      priceId: "",
      pageConfiguration: createEmptyPageConfiguration(),
      customConsents: [],
      offerAcceptedNextStepId: "",
      offerRejectedNextStepId: "",
    },
  } as EditableStep;
};

const sortStepsByFunnelRules = (steps: EditableStep[]): EditableStep[] => {
  const landingSteps = steps.filter((step) => step.type === FunnelStepType.Landing);
  const checkoutSteps = steps.filter((step) => step.type === FunnelStepType.Checkout);
  const middleSteps = steps.filter(
    (step) => step.type === FunnelStepType.Upsell || step.type === FunnelStepType.Downsell,
  );
  const thankYouSteps = steps.filter((step) => step.type === FunnelStepType.ThankYou);
  const otherSteps = steps.filter(
    (step) =>
      step.type !== FunnelStepType.Landing &&
      step.type !== FunnelStepType.Checkout &&
      step.type !== FunnelStepType.Upsell &&
      step.type !== FunnelStepType.Downsell &&
      step.type !== FunnelStepType.ThankYou,
  );

  return [...landingSteps, ...checkoutSteps, ...middleSteps, ...otherSteps, ...thankYouSteps];
};

const ensureRequiredFunnelSteps = (steps: EditableStep[], productId: string): EditableStep[] => {
  const nextSteps = [...steps];

  if (!nextSteps.some((step) => step.type === FunnelStepType.Landing)) {
    nextSteps.unshift(createStep(FunnelStepType.Landing, productId, 1));
  }

  if (!nextSteps.some((step) => step.type === FunnelStepType.Checkout)) {
    const landingCount = nextSteps.filter((step) => step.type === FunnelStepType.Landing).length;
    nextSteps.splice(landingCount, 0, createStep(FunnelStepType.Checkout, productId, landingCount + 1));
  }

  if (!nextSteps.some((step) => step.type === FunnelStepType.ThankYou)) {
    nextSteps.push(createStep(FunnelStepType.ThankYou, productId, nextSteps.length + 1));
  }

  return sortStepsByFunnelRules(nextSteps);
};

const toInitialModel = (funnel: SalesFunnel | null | undefined, productId: string): SalesFunnelFormModel => ({
  activeUntil: funnel?.activeUntil ?? null,
  cartSettings: (funnel?.cartSettings as Record<string, unknown> | null) ?? null,
  default: Boolean(funnel?.default),
  excluded: Boolean(funnel?.excluded),
  metadata: (funnel?.metadata as Record<string, unknown> | null) ?? {},
  name: funnel?.name ?? "",
  slug: funnel?.slug ?? "",
  status: funnel?.status ?? CheckoutVariantStatus.Inactive,
  steps: ensureRequiredFunnelSteps(funnel?.steps?.length ? funnel.steps : [], productId),
  target: funnel?.target ?? CheckoutVariantTarget.New,
});

/** A checkout step's setting left to the cart settings / the product. */
const DEFAULT_SETTING = "default" as const;

const PHONE_FIELD_OPTIONS: { value: CheckoutStepPhoneField | typeof DEFAULT_SETTING; label: string }[] = [
  { value: DEFAULT_SETTING, label: "Jak w ustawieniach koszyka" },
  { value: "required", label: "Wymagany" },
  { value: "optional", label: "Opcjonalny" },
  { value: "hidden", label: "Ukryty" },
];

const OWN_CROSS_SELL = "own" as const;

const CROSS_SELL_OPTIONS: { value: typeof OWN_CROSS_SELL | typeof DEFAULT_SETTING; label: string }[] = [
  { value: DEFAULT_SETTING, label: "Jak w produkcie" },
  { value: OWN_CROSS_SELL, label: "Własne" },
];

const patchStepConfig = <Step extends EditableStep>(step: Step, patch: Partial<Step["stepConfig"]>): Step =>
  ({
    ...step,
    updatedAt: new Date().toISOString(),
    stepConfig: {
      ...step.stepConfig,
      ...patch,
    },
  }) as Step;

const SortableStepItem = ({
  active,
  draggable,
  step,
  onSelect,
}: {
  active: boolean;
  draggable: boolean;
  onSelect: () => void;
  step: EditableStep;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: step.id,
    disabled: !draggable,
  });

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`flex items-center gap-2 rounded-lg border bg-white px-2 py-2 ${
        active ? "border-[#a18d6d] bg-[#fbf8f2]" : "border-zinc-200"
      } ${isDragging ? "opacity-60 shadow-lg" : ""}`}
    >
      <button
        type="button"
        className={`rounded px-2 py-1 ${
          draggable ? "cursor-grab text-zinc-400 active:cursor-grabbing" : "cursor-not-allowed text-zinc-200"
        }`}
        {...(draggable ? attributes : {})}
        {...(draggable ? listeners : {})}
      >
        <HolderOutlined />
      </button>
      <button type="button" className="min-w-0 flex-1 text-left" onClick={onSelect}>
        <Typography.Text strong className="block truncate">
          {step.name || step.id}
        </Typography.Text>
        <Typography.Text type="secondary" className="text-xs">
          {step.type} · /{normalizeFunnelStepPath(step.path)}
        </Typography.Text>
      </button>
      <Tag className="m-0">{step.type.replace("_", " ")}</Tag>
    </div>
  );
};

const PageConfigurationEditor = ({
  value,
  onChange,
}: {
  onChange: (value: PageConfiguration | null) => void;
  value: PageConfiguration | null;
}) => {
  const config = value ?? createEmptyPageConfiguration();
  const promoVideoEnabled = Boolean(config.promoVideo);
  const timerEnabled = Boolean(config.timer);

  const update = (patch: Partial<PageConfiguration>) => onChange({ ...config, ...patch });

  return (
    <div className="space-y-4">
      <Form.Item label="Nazwa produktu / oferta">
        <Input
          value={config.productNameOverride ?? ""}
          onChange={(event) => update({ productNameOverride: event.target.value })}
        />
      </Form.Item>
      <Form.Item label="URL obrazka">
        <Input
          value={config.productImageOverride ?? ""}
          onChange={(event) => update({ productImageOverride: event.target.value })}
        />
      </Form.Item>
      <Form.Item label="Opis">
        <Input.TextArea
          rows={3}
          value={config.productDescriptionOverride ?? ""}
          onChange={(event) => update({ productDescriptionOverride: event.target.value })}
        />
      </Form.Item>
      <div className="grid grid-cols-2 gap-4">
        <Form.Item label="Ukryj cennik">
          <Switch
            checked={Boolean(config.pricingDisabled)}
            onChange={(checked) => update({ pricingDisabled: checked })}
          />
        </Form.Item>
        <Form.Item label="Video nad hero">
          <Switch checked={Boolean(config.videoOnTop)} onChange={(checked) => update({ videoOnTop: checked })} />
        </Form.Item>
      </div>
      <Form.Item label="Timer / hero">
        <Switch
          checked={timerEnabled}
          onChange={(checked) =>
            update({
              timer: checked
                ? {
                    title: "Oferta specjalna",
                    description: "Opis oferty",
                    startInfo: "Oferta kończy się za:",
                  }
                : false,
            })
          }
        />
      </Form.Item>
      {config.timer ? (
        <div className="rounded-lg border border-zinc-200 p-3">
          <Form.Item label="Tytuł timera">
            <Input
              value={config.timer.title}
              onChange={(event) =>
                update({
                  timer: {
                    ...getTimerConfig(config),
                    title: event.target.value,
                  },
                })
              }
            />
          </Form.Item>
          <Form.Item label="Opis timera">
            <Input.TextArea
              rows={2}
              value={config.timer.description}
              onChange={(event) =>
                update({
                  timer: {
                    ...getTimerConfig(config),
                    description: event.target.value,
                  },
                })
              }
            />
          </Form.Item>
          <Form.Item label="Tekst nad licznikiem">
            <Input
              value={config.timer.startInfo}
              onChange={(event) =>
                update({
                  timer: {
                    ...getTimerConfig(config),
                    startInfo: event.target.value,
                  },
                })
              }
            />
          </Form.Item>
        </div>
      ) : null}
      <Form.Item label="Promo video">
        <Switch
          checked={promoVideoEnabled}
          onChange={(checked) =>
            update({
              promoVideo: checked ? { title: "Zobacz ofertę", videoId: "", description: "" } : undefined,
            })
          }
        />
      </Form.Item>
      {config.promoVideo ? (
        <div className="rounded-lg border border-zinc-200 p-3">
          <Form.Item label="Tytuł video">
            <Input
              value={config.promoVideo.title}
              onChange={(event) =>
                update({
                  promoVideo: {
                    ...config.promoVideo!,
                    title: event.target.value,
                  },
                })
              }
            />
          </Form.Item>
          <Form.Item label="Video ID">
            <Input
              value={config.promoVideo.videoId}
              onChange={(event) =>
                update({
                  promoVideo: {
                    ...config.promoVideo!,
                    videoId: event.target.value,
                  },
                })
              }
            />
          </Form.Item>
          <Form.Item label="Opis video">
            <Input.TextArea
              rows={2}
              value={config.promoVideo.description ?? ""}
              onChange={(event) =>
                update({
                  promoVideo: {
                    ...config.promoVideo!,
                    description: event.target.value,
                  },
                })
              }
            />
          </Form.Item>
        </div>
      ) : null}
      <Button block onClick={() => onChange(null)}>
        Wyczyść page config
      </Button>
    </div>
  );
};

export const ProductSalesFunnelForm = ({
  funnel,
  isSaving,
  onCancel,
  onSubmit,
  open,
  productId,
}: ProductSalesFunnelFormProps) => {
  const [model, setModel] = useState<SalesFunnelFormModel>(() => toInitialModel(funnel, productId));
  const [selectedStepId, setSelectedStepId] = useState(model.steps[0]?.id ?? null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const priceOptions = useProductPricesOptions(undefined, true);
  const productDetails = useAppSelector((store) => store.product.details);
  const products = useAppSelector((store) => store.products.productsListData);
  const crossSellOptions = products
    .filter(
      (product) =>
        (product.type === ProductType.ONLY_CONTENT || product.type === ProductType.ADDON) && product.id !== productId,
    )
    .map((product) => ({ label: product.name, value: product.id }));

  const selectedStep = useMemo(
    () => model.steps.find((step) => step.id === selectedStepId) ?? model.steps[0] ?? null,
    [model.steps, selectedStepId],
  );

  const stepOptions = model.steps.map((step) => ({
    label: `${step.name} (${step.path})`,
    value: step.id,
  }));
  const productOptions = productDetails
    ? [
        {
          label: `${productDetails.name} (${productDetails.slug || productDetails.id})`,
          value: productDetails.id,
        },
      ]
    : [{ label: productId, value: productId }];
  const draggableSteps = model.steps.filter(
    (step) => step.type === FunnelStepType.Upsell || step.type === FunnelStepType.Downsell,
  );
  const draggableStepIds = draggableSteps.map((step) => step.id);

  const patchModel = (patch: Partial<SalesFunnelFormModel>) =>
    setModel((current) => ({
      ...current,
      ...patch,
      steps: patch.steps ? sortStepsByFunnelRules(patch.steps) : current.steps,
    }));
  const updateStep = (stepId: string, updater: (step: EditableStep) => EditableStep) => {
    setModel((current) => ({
      ...current,
      steps: current.steps.map((step) => (step.id === stepId ? updater(step) : step)),
    }));
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) {
      return;
    }

    setModel((current) => {
      const sortableMiddleSteps = current.steps.filter(
        (step) => step.type === FunnelStepType.Upsell || step.type === FunnelStepType.Downsell,
      );
      const oldIndex = sortableMiddleSteps.findIndex((step) => step.id === active.id);
      const newIndex = sortableMiddleSteps.findIndex((step) => step.id === over.id);

      if (oldIndex === -1 || newIndex === -1) {
        return current;
      }

      const sortedMiddleSteps = arrayMove(sortableMiddleSteps, oldIndex, newIndex);
      const middleById = new Map(sortedMiddleSteps.map((step) => [step.id, step]));
      const reorderedSteps = current.steps.map((step) => middleById.get(step.id) ?? step);

      return { ...current, steps: sortStepsByFunnelRules(reorderedSteps) };
    });
  };

  const submit = () => {
    onSubmit({
      ...model,
      slug: slugifyForSave(model.slug || model.name),
      steps: ensureRequiredFunnelSteps(
        model.steps.map((step) => ({
          ...step,
          path: normalizeFunnelStepPath(step.path),
        })) as EditableStep[],
        productId,
      ),
    });
  };

  return (
    <Modal
      open={open}
      width={1200}
      title={funnel ? `Edytuj sales funnel: ${funnel.name}` : "Nowy sales funnel"}
      onCancel={onCancel}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Anuluj
        </Button>,
        <Button key="save" type="primary" icon={<SaveOutlined />} loading={isSaving} onClick={submit}>
          Zapisz
        </Button>,
      ]}
    >
      <div className="grid gap-6 lg:grid-cols-[360px_minmax(0,1fr)]">
        <div className="space-y-4">
          <div className="rounded-lg border border-zinc-200 p-4">
            <Typography.Title level={5} className="!mt-0">
              Ustawienia
            </Typography.Title>
            <Form layout="vertical">
              <Form.Item label="Nazwa" required>
                <Input value={model.name} onChange={(event) => patchModel({ name: event.target.value })} />
              </Form.Item>
              <Form.Item label="Slug" required>
                <Input
                  value={model.slug}
                  onChange={(event) =>
                    patchModel({
                      slug: slugifyWhileTyping(event.target.value),
                    })
                  }
                />
              </Form.Item>
              <div className="grid grid-cols-2 gap-3">
                <Form.Item label="Status">
                  <Select
                    value={model.status}
                    onChange={(status) => patchModel({ status })}
                    options={[
                      { label: "Active", value: CheckoutVariantStatus.Active },
                      {
                        label: "Inactive",
                        value: CheckoutVariantStatus.Inactive,
                      },
                    ]}
                  />
                </Form.Item>
                <Form.Item label="Target">
                  <Select
                    value={model.target}
                    onChange={(target) => patchModel({ target })}
                    options={[
                      { label: "New", value: CheckoutVariantTarget.New },
                      {
                        label: "Renewal",
                        value: CheckoutVariantTarget.Renewal,
                      },
                      { label: "All", value: CheckoutVariantTarget.All },
                    ]}
                  />
                </Form.Item>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Form.Item label="Domyślny">
                  <Switch checked={model.default} onChange={(checked) => patchModel({ default: checked })} />
                </Form.Item>
                <Form.Item label="Wykluczony">
                  <Switch checked={model.excluded} onChange={(checked) => patchModel({ excluded: checked })} />
                </Form.Item>
              </div>
              <Form.Item label="Aktywny do">
                <DatePicker
                  className="w-full"
                  showTime
                  value={model.activeUntil ? dayjs.unix(model.activeUntil) : null}
                  onChange={(value) => patchModel({ activeUntil: value ? value.unix() : null })}
                />
              </Form.Item>
            </Form>
          </div>

          <div className="rounded-lg border border-zinc-200 p-4">
            <div className="mb-3 flex items-center justify-between">
              <Typography.Title level={5} className="!m-0">
                Kroki
              </Typography.Title>
              <Select
                className="w-[150px]"
                size="small"
                placeholder="Dodaj"
                value={null}
                onChange={(type: FunnelStepType) => {
                  const step = createStep(type, productId, model.steps.length + 1);
                  patchModel({ steps: [...model.steps, step] });
                  setSelectedStepId(step.id);
                }}
                options={[
                  {
                    disabled: model.steps.some((step) => step.type === FunnelStepType.Landing),
                    label: "Landing",
                    value: FunnelStepType.Landing,
                  },
                  {
                    disabled: model.steps.some((step) => step.type === FunnelStepType.Checkout),
                    label: "Checkout",
                    value: FunnelStepType.Checkout,
                  },
                  { label: "Upsell", value: FunnelStepType.Upsell },
                  { label: "Downsell", value: FunnelStepType.Downsell },
                  {
                    disabled: model.steps.some((step) => step.type === FunnelStepType.ThankYou),
                    label: "Thank you",
                    value: FunnelStepType.ThankYou,
                  },
                ]}
              />
            </div>
            <Typography.Text type="secondary" className="mb-3 block text-xs">
              Landing, checkout i thank you mają stałe pozycje. Przeciągać można tylko kroki upsell/downsell między
              checkoutem a thank you.
            </Typography.Text>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={draggableStepIds} strategy={verticalListSortingStrategy}>
                <Space direction="vertical" className="w-full">
                  {model.steps.map((step) => (
                    <SortableStepItem
                      key={step.id}
                      active={selectedStep?.id === step.id}
                      draggable={draggableStepIds.includes(step.id)}
                      step={step}
                      onSelect={() => setSelectedStepId(step.id)}
                    />
                  ))}
                </Space>
              </SortableContext>
            </DndContext>
          </div>
        </div>

        {selectedStep ? (
          <div className="rounded-lg border border-zinc-200 p-4">
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <Typography.Title level={4} className="!m-0">
                  {selectedStep.name || "Krok"}
                </Typography.Title>
                <Typography.Text type="secondary">{selectedStep.type}</Typography.Text>
              </div>
              <Button
                danger
                icon={<DeleteOutlined />}
                disabled={
                  selectedStep.type === FunnelStepType.Landing ||
                  selectedStep.type === FunnelStepType.Checkout ||
                  selectedStep.type === FunnelStepType.ThankYou
                }
                onClick={() => {
                  const nextSteps = model.steps.filter((step) => step.id !== selectedStep.id);
                  patchModel({ steps: nextSteps });
                  setSelectedStepId(nextSteps[0]?.id ?? null);
                }}
              >
                Usuń krok
              </Button>
            </div>

            <Tabs
              items={[
                {
                  key: "basic",
                  label: "Krok",
                  children: (
                    <Form layout="vertical">
                      <div className="grid grid-cols-2 gap-4">
                        <Form.Item label="Nazwa" required>
                          <Input
                            value={selectedStep.name}
                            onChange={(event) =>
                              updateStep(selectedStep.id, (step) => ({
                                ...step,
                                name: event.target.value,
                              }))
                            }
                          />
                        </Form.Item>
                        <Form.Item label="Path" required>
                          <Input
                            addonBefore="/"
                            value={selectedStep.path}
                            onChange={(event) =>
                              updateStep(selectedStep.id, (step) => ({
                                ...step,
                                path: normalizeFunnelStepPath(event.target.value),
                              }))
                            }
                          />
                        </Form.Item>
                      </div>
                      <Form.Item label="Opis">
                        <Input.TextArea
                          rows={3}
                          value={selectedStep.description ?? ""}
                          onChange={(event) =>
                            updateStep(selectedStep.id, (step) => ({
                              ...step,
                              description: event.target.value,
                            }))
                          }
                        />
                      </Form.Item>
                      <Form.Item label="Typ kroku">
                        <Select
                          value={selectedStep.type}
                          disabled={
                            selectedStep.type === FunnelStepType.Landing ||
                            selectedStep.type === FunnelStepType.Checkout ||
                            selectedStep.type === FunnelStepType.ThankYou
                          }
                          onChange={(type: FunnelStepType) => {
                            if (type !== FunnelStepType.Upsell && type !== FunnelStepType.Downsell) {
                              return;
                            }

                            updateStep(selectedStep.id, () => ({
                              ...createStep(type, productId, model.steps.length),
                              id: selectedStep.id,
                              name: selectedStep.name,
                              path: selectedStep.path,
                              createdAt: selectedStep.createdAt,
                            }));
                          }}
                          options={[
                            { label: "Upsell", value: FunnelStepType.Upsell },
                            {
                              label: "Downsell",
                              value: FunnelStepType.Downsell,
                            },
                          ]}
                        />
                      </Form.Item>
                      {selectedStep.type === FunnelStepType.Landing ||
                      selectedStep.type === FunnelStepType.Checkout ||
                      selectedStep.type === FunnelStepType.ThankYou ? (
                        <Typography.Text type="secondary" className="text-xs">
                          Typ tego kroku jest stały w strukturze funnelu. Zmienialne są tylko typy upsell/downsell.
                        </Typography.Text>
                      ) : null}
                    </Form>
                  ),
                },
                {
                  key: "config",
                  label: "Konfiguracja",
                  children: (
                    <Form layout="vertical">
                      <Form.Item label="Szablon strony">
                        <FunnelStepPageSelect
                          step={selectedStep}
                          funnelSlug={model.slug}
                          onChange={(patch) => updateStep(selectedStep.id, (step) => patchStepConfig(step, patch))}
                        />
                      </Form.Item>

                      {"productId" in selectedStep.stepConfig ? (
                        <Form.Item label="Product ID">
                          <Select
                            showSearch
                            value={selectedStep.stepConfig.productId}
                            options={productOptions}
                            optionFilterProp="label"
                            onChange={(selectedProductId) =>
                              updateStep(selectedStep.id, (step) =>
                                patchStepConfig(step, {
                                  productId: selectedProductId,
                                }),
                              )
                            }
                          />
                        </Form.Item>
                      ) : null}

                      {selectedStep.type === FunnelStepType.Landing ? (
                        <>
                          <CohortSeatsFields
                            value={selectedStep.stepConfig.cohortSeats}
                            onChange={(cohortSeats) =>
                              updateStep(selectedStep.id, (step) => patchStepConfig(step, { cohortSeats }))
                            }
                          />
                          <Typography.Title level={5}>Cena przed obniżką</Typography.Title>
                          <div className="grid grid-cols-2 gap-4">
                            <Form.Item label="Przekreślona cena">
                              <InputNumber
                                min={1}
                                precision={0}
                                addonAfter="zł"
                                placeholder="Brak"
                                className="w-full"
                                value={selectedStep.stepConfig.strikethroughPrice ?? null}
                                onChange={(strikethroughPrice) =>
                                  updateStep(selectedStep.id, (step) =>
                                    patchStepConfig(step, { strikethroughPrice: strikethroughPrice ?? null }),
                                  )
                                }
                              />
                            </Form.Item>
                          </div>
                          <RewardFields
                            value={selectedStep.stepConfig.reward}
                            onChange={(reward) =>
                              updateStep(selectedStep.id, (step) => patchStepConfig(step, { reward }))
                            }
                          />
                        </>
                      ) : null}

                      {selectedStep.type === FunnelStepType.Checkout ? (
                        <>
                          <Form.Item label="Ceny w checkout">
                            <Select
                              mode="multiple"
                              showSearch
                              optionFilterProp="label"
                              value={selectedStep.stepConfig.prices}
                              options={priceOptions}
                              onChange={(prices) =>
                                updateStep(selectedStep.id, (step) => patchStepConfig(step, { prices }))
                              }
                            />
                          </Form.Item>
                          <div className="grid grid-cols-2 gap-4">
                            <Form.Item label="Telefon">
                              <Select<CheckoutStepPhoneField | typeof DEFAULT_SETTING>
                                value={selectedStep.stepConfig.phoneField ?? DEFAULT_SETTING}
                                options={PHONE_FIELD_OPTIONS}
                                onChange={(phoneField) =>
                                  updateStep(selectedStep.id, (step) =>
                                    patchStepConfig(step, {
                                      phoneField: phoneField === DEFAULT_SETTING ? null : phoneField,
                                    }),
                                  )
                                }
                              />
                            </Form.Item>
                            <Form.Item label="Order bump">
                              <Select<typeof OWN_CROSS_SELL | typeof DEFAULT_SETTING>
                                value={selectedStep.stepConfig.crossSell ? OWN_CROSS_SELL : DEFAULT_SETTING}
                                options={CROSS_SELL_OPTIONS}
                                onChange={(crossSell) =>
                                  updateStep(selectedStep.id, (step) =>
                                    patchStepConfig(step, {
                                      crossSell: crossSell === DEFAULT_SETTING ? null : { products: [] },
                                    }),
                                  )
                                }
                              />
                            </Form.Item>
                          </div>
                          {selectedStep.stepConfig.crossSell ? (
                            <Form.Item label="Bumpy w tym checkoucie">
                              <Select
                                mode="multiple"
                                showSearch
                                optionFilterProp="label"
                                placeholder="Bez bumpa"
                                value={selectedStep.stepConfig.crossSell.products.map((product) => product.productId)}
                                options={crossSellOptions}
                                onChange={(productIds: string[]) =>
                                  updateStep(selectedStep.id, (step) =>
                                    patchStepConfig(step, {
                                      crossSell: {
                                        // A bump that stays selected keeps its pinned price and label.
                                        products: productIds.map(
                                          (id) =>
                                            selectedStep.stepConfig.crossSell?.products.find(
                                              (bump) => bump.productId === id,
                                            ) ?? {
                                              productId: id,
                                              productName: products.find((product) => product.id === id)?.name ?? id,
                                            },
                                        ),
                                      },
                                    }),
                                  )
                                }
                              />
                            </Form.Item>
                          ) : null}
                        </>
                      ) : null}

                      {(selectedStep.type === FunnelStepType.Upsell ||
                        selectedStep.type === FunnelStepType.Downsell) && (
                        <>
                          <Form.Item label="Cena oferty">
                            <Select
                              showSearch
                              optionFilterProp="label"
                              value={selectedStep.stepConfig.priceId || undefined}
                              options={priceOptions}
                              onChange={(priceId) =>
                                updateStep(selectedStep.id, (step) => patchStepConfig(step, { priceId }))
                              }
                            />
                          </Form.Item>
                          <div className="grid grid-cols-2 gap-4">
                            <Form.Item label="Następny po akceptacji">
                              <Select
                                allowClear
                                value={selectedStep.stepConfig.offerAcceptedNextStepId || undefined}
                                options={stepOptions.filter((option) => option.value !== selectedStep.id)}
                                onChange={(offerAcceptedNextStepId) =>
                                  updateStep(selectedStep.id, (step) =>
                                    patchStepConfig(step, {
                                      offerAcceptedNextStepId: offerAcceptedNextStepId ?? "",
                                    }),
                                  )
                                }
                              />
                            </Form.Item>
                            <Form.Item label="Następny po odrzuceniu">
                              <Select
                                allowClear
                                value={selectedStep.stepConfig.offerRejectedNextStepId || undefined}
                                options={stepOptions.filter((option) => option.value !== selectedStep.id)}
                                onChange={(offerRejectedNextStepId) =>
                                  updateStep(selectedStep.id, (step) =>
                                    patchStepConfig(step, {
                                      offerRejectedNextStepId: offerRejectedNextStepId ?? "",
                                    }),
                                  )
                                }
                              />
                            </Form.Item>
                          </div>
                        </>
                      )}
                    </Form>
                  ),
                },
                {
                  key: "page",
                  label: "Zawartość strony",
                  children: (
                    <PageConfigurationEditor
                      value={selectedStep.stepConfig.pageConfiguration ?? null}
                      onChange={(pageConfiguration) =>
                        updateStep(selectedStep.id, (step) => patchStepConfig(step, { pageConfiguration }))
                      }
                    />
                  ),
                },
              ]}
            />
          </div>
        ) : (
          <div className="flex min-h-[360px] items-center justify-center rounded-lg border border-dashed border-zinc-300">
            <Button
              icon={<PlusOutlined />}
              onClick={() => {
                const step = createStep(FunnelStepType.Checkout, productId, 1);
                patchModel({ steps: [step] });
                setSelectedStepId(step.id);
              }}
            >
              Dodaj pierwszy krok
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
};
